<template>
    <div class="lms__superadmin_users_list">
        <div class="lms__superadmin_users_header">
            <component-induction-message
                :title="'Users'"
                :content="'View, manage and invite people in this workspace. Configure their roles to make sure they’re seeing the right content, at the right time.'"
                :link-to="''"
                :theme="'secondary'"
                :route-name="'lms-superadmin-user-list'"
            ></component-induction-message>
            <view-actions
                :title="'Users'"
                :custom-button-text="'Invite user'"
                :custom-button-active="user.isSuperadmin() || user.isMasterAdministrator() || user.isUserAdministrator() || user.isOrgUnitManager()"
                :custom-button-theme="'default'"
                :custom-button-list-active="user.isSuperadmin() || user.isMasterAdministrator()"
                :custom-button-list-items="customButtonListItems"
                :submit-button-text="'Bulk actions'"
                :submit-button-active="user.isSuperadmin() || user.isMasterAdministrator() || user.isUserAdministrator()"
                :submit-button-theme="'primary'"
                :submit-button-list-active="true"
                :submit-button-list-items="submitButtonListItems"
                :cancel-button-active="false"
                @emit-custom="redirectInviteUser"
                @emit-submit="redirectBulkAction"
                @emit-add-users-to-group="redirectAddUsersToGroup"
                @emit-remove-users-from-group="redirectRemoveUsersFromGroup"
                @emit-delete-profiles="redirectDeleteProfiles"
                @emit-reactivate-profiles="redirectReactivateProfiles"
                @emit-resend-invitations="redirectResendInvitations"
                @emit-cancel-invitations="redirectCancelInvitations"
                @emit-invite-superadmin="redirectInviteSuperadmin"
                @emit-invite-masteradmin="redirectInviteMasteradmin"
                @emit-bulk-invite-users="redirectBulkInviteUsers"
            />
            <div class="lms__superadmin_users_main">
                <component-list
                    ref="componentlist"
                    :component-table="LmsSuperadminUsersListTable"
                    :component-table-name="'users'"
                    :filter-search-queryable="true"
                    :filter-select-active="Boolean(filtersSelect.length)"
                    :filter-select-items="filtersSelect"
                    :get-pagination-getter="getPaginationGetter"
                    :get-items-getter="getItemsGetter"
                    :get-items-dispatch="getItemsDispatch"
                    :get-items-dispatch-trigger-name="'trigger-get-users'"
                    :get-items-dispatch-params="loggedInUserWorkspaceId ? { workspaceId: loggedInUserWorkspaceId } : ''"
                    :force-loading-trigger-name="'trigger-loading-users'"
                    :filter-select-prefix="loggedInUserWorkspaceId ? '' : 'filters'"
                    :item-name="['user', 'users']"
                    :default-page-size="12"
                    :table-preferences-active="true"
                    @emit-request-finally="stopOrgUnitsApplyLoading"
                ></component-list>
            </div>
        </div>
    </div>
</template>

<script>
import { performSubmitValidatePromise, validateBeforeRouteLeave } from '@/helpers/mixins'
import LmsSuperadminUsersListTable from '@/components/lms/superadmin/users/list/LmsSuperadminUsersListTable'
import ComponentList from '@/components/default/shared/ComponentList'
import ViewActions from '@/components/default/shared/ViewActions'
import { User } from '@/User'
import ComponentInductionMessage from '@/components/default/shared/ComponentInductionMessage'

export default {
  name: 'lms-superadmin-users-list',
  components: { ComponentInductionMessage, ViewActions, ComponentList },
  data () {
    return {
      user: new User(),
      LmsSuperadminUsersListTable,
      listExtraFilters: ''
    }
  },
  mixins: [validateBeforeRouteLeave, performSubmitValidatePromise],
  computed: {
    customButtonListItems () {
      let listArr = []

      if (this.user.isSuperadmin()) {
        listArr.push({
          key: 'invite-superadmin',
          name: 'Invite super admin'
        })
      }
      if (this.user.isSuperadmin() || this.user.isMasterAdministrator()) {
        listArr.push({
          key: 'invite-masteradmin',
          name: 'Invite master admin'
        })
      }

      return listArr
    },
    submitButtonListItems () {
      let listArr = []

      if (this.loggedInUserWorkspaceId) {
        listArr.push({
          key: 'add-users-to-group',
          name: 'Add users to group'
        })
        listArr.push({
          key: 'remove-users-from-group',
          name: 'Remove users from group'
        })
        listArr.push({
          key: 'delete-profiles',
          name: 'Archive profiles'
        })
        listArr.push({
          key: 'reactivate-profiles',
          name: 'Reactivate profiles'
        })
        listArr.push({
          key: 'resend-invitations',
          name: 'Resend invitations'
        })
        listArr.push({
          key: 'cancel-invitations',
          name: 'Cancel invitations'
        })
      }
      listArr.push({
        key: 'bulk-invite-users',
        name: 'Bulk invite users'
      })

      return listArr
    },
    loggedInUserWorkspaceId () {
      return this.$store.getters.getAuthUserSecondStageTokenDataWorkspaceId
    },
    getPaginationGetter () {
      let output

      if (this.loggedInUserWorkspaceId) {
        output = 'getLmsSuperadminWorkspaceUsersPagination'
      } else {
        output = 'getLmsSuperadminUsersPagination'
      }

      return output
    },
    getItemsGetter () {
      let output

      if (this.loggedInUserWorkspaceId) {
        output = 'getLmsSuperadminWorkspaceUsers'
      } else {
        output = 'getLmsSuperadminUsers'
      }

      return output
    },
    getItemsDispatch () {
      let output

      if (this.loggedInUserWorkspaceId) {
        output = 'lmsSuperadminWorkspaceUsersGet'
      } else {
        output = 'lmsSuperadminUsersGet'
      }

      return output
    },
    routeOrgUnitsQuery () {
      return this.$route.query ? this.$route.query.orgUnits : undefined
    },
    routeOrgUnitsQueryMapped () {
      const unitsRaw = this.routeOrgUnitsQuery
      const unitsMapped = unitsRaw ? unitsRaw.split(',') : []
      let unitsArr = []

      for (let unit of unitsMapped) {
        unitsArr.push({
          name: unit.split(';')[1],
          id: unit.split(';')[0]
        })
      }

      return unitsArr
    },
    routeGroupsQuery () {
      return this.$route.query ? this.$route.query.groups : undefined
    },
    routeGroupsQueryMapped () {
      const groupsRaw = this.routeGroupsQuery
      const groupsMapped = groupsRaw ? groupsRaw.split(',') : []
      let groupsArr = []

      for (let group of groupsMapped) {
        groupsArr.push({
          label: group.split(';')[1],
          value: group.split(';')[0]
        })
      }

      return groupsArr
    },
    routeProfileStatusQuery () {
      return this.$route.query ? this.$route.query.profileStatus : undefined
    },
    loggedInUserProfilesWithMAorUARoles () {
      return this.$store.getters.getLmsSuperadminUserProfilesWithUAOrMARole
    },
    loggedInUserProfilesCustomersWithMARole () {
      return this.$store.getters.getLmsSuperadminUserProfilesCustomersWithMARole
    },
    filtersSelect () {
      let tableArr = []

      if (this.loggedInUserWorkspaceId) {
        tableArr.push({
          key: 'roles',
          label: 'Roles',
          type: 'multiselect',
          getItemsGetter: 'getLmsSuperadminCustomerRolesMappedIdName',
          getItemsGetterMapped: 'getLmsSuperadminCustomerRoles',
          getItemsDispatch: 'lmsSuperadminCustomerRolesGet',
          itemName: ['role', 'roles'],
          tableLabels: [
            {
              key: 'name',
              title: 'Name',
              type: 'string',
              sortable: true,
              queryable: true
            }
          ]
        })
        tableArr.push({
          key: 'units',
          label: 'Organisation unit',
          type: 'org-units',
          preselected: this.routeOrgUnitsQueryMapped,
          allowed: this.user.listOrgUnitManagerUnits()
        })
        tableArr.push({
          key: 'groups',
          label: 'Groups',
          type: 'multiselect',
          getItemsGetter: 'getLmsSuperadminGroupsMappedIdName',
          getItemsGetterMapped: 'getLmsSuperadminGroupsMappedLabelValue',
          getItemsDispatch: 'lmsSuperadminGroupsGet',
          itemName: ['group', 'groups'],
          searchQueryable: true,
          preselected: this.routeGroupsQueryMapped,
          tableLabels: [
            {
              key: 'name',
              title: 'Name',
              type: 'string',
              sortable: true,
              queryable: true
            }
          ]
        })
        tableArr.push({
          key: 'status',
          label: 'Status',
          type: 'checkbox',
          preselected: this.routeProfileStatusQuery ? [this.routeProfileStatusQuery] : ['active', 'invited'],
          values: [
            {
              label: 'Active',
              value: 'active'
            },
            {
              label: 'Invited',
              value: 'invited'
            },
            {
              label: 'Rejected',
              value: 'rejected'
            },
            {
              label: 'Cancelled',
              value: 'cancelled'
            },
            {
              label: 'Archived',
              value: 'deleted'
            },
            {
              label: 'Expired',
              value: 'expired'
            }
          ]
        })
      }

      if (
        (
          this.user.isSuperadmin() ||
      this.loggedInUserProfilesCustomersWithMARole.length > 1
        ) &&
      !this.loggedInUserWorkspaceId
      ) {
        tableArr.push({
          key: 'customer',
          label: 'Customer',
          type: 'multiselect',
          getItemsGetter: 'getLmsSuperadminCustomersMappedIdNameStatus',
          getItemsGetterMapped: 'getLmsSuperadminCustomersMappedLabelValue',
          getItemsDispatch: 'lmsSuperadminCustomersGet',
          getPaginationGetter: 'getLmsSuperadminCustomersPagination',
          itemName: ['customer', 'customers'],
          searchQueryable: true,
          tableLabels: [
            {
              key: 'name',
              title: 'Name',
              type: 'string',
              sortable: true,
              queryable: true
            }
          ]
        })
      }

      if (
        (
          this.user.isSuperadmin() ||
      this.user.isMasterAdministrator()
        ) &&
      !this.loggedInUserWorkspaceId
      ) {
        tableArr.push({
          key: 'workspace',
          label: 'Workspace',
          type: 'multiselect',
          getItemsGetter: 'getLmsSuperadminWorkspacesMappedIdNameStatus',
          getItemsGetterMapped: 'getLmsSuperadminWorkspacesMappedLabelValue',
          getItemsDispatch: 'lmsSuperadminWorkspacesGet',
          getPaginationGetter: 'getLmsSuperadminWorkspacesPagination',
          itemName: ['workspace', 'workspaces'],
          searchQueryable: true,
          tableLabels: [
            {
              key: 'name',
              title: 'Name',
              type: 'string',
              sortable: true,
              queryable: true
            },
            {
              key: 'customer',
              title: 'Customer',
              type: 'string'
            }
          ]
        })
      }

      return tableArr
    }
  },
  methods: {
    redirectInviteUser () {
      this.$router.push({ name: 'lms-superadmin-user-profile-add' })
    },
    redirectBulkAction () {
      this.$router.push({ name: 'lms-superadmin-user-bulk-actions' })
    },
    redirectAddUsersToGroup () {
      this.$router.push({ name: 'lms-superadmin-user-bulk-actions-add-to-groups' })
    },
    redirectRemoveUsersFromGroup () {
      this.$router.push({ name: 'lms-superadmin-user-bulk-actions-remove-from-groups' })
    },
    redirectDeleteProfiles () {
      this.$router.push({ name: 'lms-superadmin-user-bulk-actions-delete-profiles' })
    },
    redirectReactivateProfiles () {
      this.$router.push({ name: 'lms-superadmin-user-bulk-actions-reactivate-profiles' })
    },
    redirectResendInvitations () {
      this.$router.push({ name: 'lms-superadmin-user-bulk-actions-resend-invitations' })
    },
    redirectCancelInvitations () {
      this.$router.push({ name: 'lms-superadmin-user-bulk-actions-cancel-invitations' })
    },
    redirectInviteSuperadmin () {
      this.$router.push({ name: 'lms-superadmin-admin-add' })
    },
    redirectInviteMasteradmin () {
      this.$router.push({ name: 'lms-superadmin-masteradmin-add' })
    },
    redirectBulkInviteUsers () {
      this.$router.push({ name: 'lms-superadmin-user-bulk-add' })
    },
    stopOrgUnitsApplyLoading () {
      this.$eventBus.$emit('stop-org-units-filter-apply')
    },
    handleListExtraFilters (data) {
      setTimeout(() => {
        this.$refs.componentlist.getItems()
      }, 100)
    }
  },
  created () {
    if (this.routeProfileStatusQuery || this.routeOrgUnitsQuery || this.routeGroupsQuery) {
      this.handleListExtraFilters()
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/lms/superadmin/users/_lmssuperadminuserslist.scss";
</style>
